import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
// Baji website backend is mounted at /api/site/* inside server.py
export const API = `${BACKEND_URL}/api/site`;

export const api = axios.create({
  baseURL: API,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

// Attach bearer token if stored (fallback for cookie-blocked envs)
api.interceptors.request.use((config) => {
  // When "Remember me" is unchecked, the token lives only in sessionStorage
  // (cleared on tab close). Otherwise it persists in localStorage.
  const token = localStorage.getItem("access_token") || sessionStorage.getItem("access_token");
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Stable device fingerprint so server can rate-limit free-spin per device.
  // Survives logout/re-login and new-account registration on the same browser.
  let deviceId = localStorage.getItem("ml_device_id");
  if (!deviceId) {
    deviceId =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `d_${Date.now()}_${Math.random().toString(36).slice(2, 10)}_${Math.random().toString(36).slice(2, 10)}`;
    try { localStorage.setItem("ml_device_id", deviceId); } catch { /* ignore */ }
  }
  config.headers["X-Device-Id"] = deviceId;
  return config;
});

export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // Pydantic 422 validation errors: [{ loc: [...], msg: "...", type: "..." }, ...]
    return detail
      .map((e) => {
        if (!e) return "";
        if (typeof e === "string") return e;
        if (typeof e.msg === "string") {
          // Strip Pydantic's "Value error, " prefix for cleaner UX
          const msg = e.msg.replace(/^Value error,\s*/i, "");
          // Add field name (last segment of loc) if available
          if (Array.isArray(e.loc) && e.loc.length > 0) {
            const field = e.loc[e.loc.length - 1];
            if (field && field !== "body") return `${field}: ${msg}`;
          }
          return msg;
        }
        return JSON.stringify(e);
      })
      .filter(Boolean)
      .join(" • ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Helper: extract the cleanest possible error message from any axios error
export function extractApiError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  // Network error (no response received)
  if (err.message === "Network Error" || !err.response) {
    return "Network error. Please check your connection and try again.";
  }
  const data = err.response?.data;
  if (!data) return fallback;
  // FastAPI normally puts errors under `detail`
  const detailMsg = formatApiErrorDetail(data.detail);
  if (detailMsg) return detailMsg;
  // Fallbacks for non-standard error shapes
  if (typeof data === "string") return data;
  if (typeof data.message === "string") return data.message;
  if (typeof data.error === "string") return data.error;
  return fallback;
}
